(function () {
  'use strict';

  const GIFT_VARIANT_ID = 52853837005146;
  const THRESHOLD_CENTS = 15000;
  const SYNC_DEBOUNCE_MS = 250;
  const DRAWER_REFRESH_DELAY_MS = 250;

  if (window.__DL_FREE_GIFT_INITIALIZED__) return;
  window.__DL_FREE_GIFT_INITIALIZED__ = true;

  let isRunning = false;
  let scheduledTimer = null;
  let internalCartRequestCount = 0;

  function isInternalCartRequest() {
    return internalCartRequestCount > 0;
  }

  function beginInternalCartRequest() {
    internalCartRequestCount += 1;
  }

  function endInternalCartRequest() {
    setTimeout(function () {
      internalCartRequestCount = Math.max(0, internalCartRequestCount - 1);
    }, 80);
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function getCart() {
    return fetch('/cart.js', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Failed to fetch cart.js');
      }

      return response.json();
    });
  }

  function addGift() {
    beginInternalCartRequest();

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: 1,
            properties: {
              _free_gift: 'true'
            }
          }
        ]
      })
    }).finally(endInternalCartRequest);
  }

  function changeLineQty(lineKey, quantity) {
    beginInternalCartRequest();

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity
      })
    }).finally(endInternalCartRequest);
  }

  function getGiftLines(cart) {
    return (cart.items || []).filter(function (item) {
      return Number(item.variant_id) === Number(GIFT_VARIANT_ID);
    });
  }

  function getGiftFinalTotal(cart) {
    return getGiftLines(cart).reduce(function (sum, item) {
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  function getEligibleTotal(cart) {
    const cartTotal = Number(cart.total_price || 0);
    const giftTotal = getGiftFinalTotal(cart);

    return Math.max(0, cartTotal - giftTotal);
  }

  async function refreshCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    try {
      const html = await fetch('?section_id=cart-drawer', {
        cache: 'no-store',
        credentials: 'same-origin'
      }).then(function (response) {
        return response.text();
      });

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('cart-drawer');

      if (newDrawer) {
        drawer.innerHTML = newDrawer.innerHTML;
      }
    } catch (error) {}
  }

  function notifyCartChanged() {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {});
      }
    } catch (error) {}

    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function removeAllGiftLines(giftLines) {
    if (!giftLines.length) return;

    await Promise.all(
      giftLines.map(function (line) {
        return changeLineQty(line.key, 0);
      })
    );
  }

  async function normalizeGiftQuantity(giftLines, giftQty) {
    if (!giftLines.length) {
      await addGift();
      return;
    }

    if (giftQty <= 1 && giftLines.length === 1) return;

    const firstGiftLine = giftLines[0];
    const extraGiftLines = giftLines.slice(1);

    await changeLineQty(firstGiftLine.key, 1);

    if (extraGiftLines.length) {
      await Promise.all(
        extraGiftLines.map(function (line) {
          return changeLineQty(line.key, 0);
        })
      );
    }
  }

  async function syncGift() {
    if (isRunning) return;

    isRunning = true;

    try {
      const cart = await getCart();
      const giftLines = getGiftLines(cart);

      const giftQty = giftLines.reduce(function (sum, item) {
        return sum + Number(item.quantity || 0);
      }, 0);

      const hasGift = giftQty > 0;
      const eligibleTotal = getEligibleTotal(cart);
      const shouldHaveGift = eligibleTotal >= THRESHOLD_CENTS;

      let changed = false;

      if (!shouldHaveGift && hasGift) {
        await removeAllGiftLines(giftLines);
        changed = true;
      }

      if (shouldHaveGift) {
        if (!hasGift || giftQty !== 1 || giftLines.length > 1) {
          await normalizeGiftQuantity(giftLines, giftQty);
          changed = true;
        }
      }

      if (changed) {
        await sleep(DRAWER_REFRESH_DELAY_MS);
        await refreshCartDrawer();
        notifyCartChanged();
      }
    } catch (error) {
      console.error('[FreeGift] sync error:', error);
    } finally {
      isRunning = false;
    }
  }

  function scheduleSync() {
    if (scheduledTimer) {
      clearTimeout(scheduledTimer);
    }

    scheduledTimer = setTimeout(function () {
      scheduledTimer = null;
      syncGift();
    }, SYNC_DEBOUNCE_MS);
  }

  function isCartEndpoint(url) {
    if (!url) return false;

    return (
      url.indexOf('/cart/add') !== -1 ||
      url.indexOf('/cart/change') !== -1 ||
      url.indexOf('/cart/update') !== -1 ||
      url.indexOf('/cart/clear') !== -1
    );
  }

  function patchFetchOnce() {
    if (window.__DL_FREE_GIFT_FETCH_PATCHED__) return;

    window.__DL_FREE_GIFT_FETCH_PATCHED__ = true;

    const originalFetch = window.fetch;

    window.fetch = async function () {
      const args = Array.prototype.slice.call(arguments);
      const response = await originalFetch.apply(this, args);

      try {
        const request = args[0];
        const url =
          typeof request === 'string'
            ? request
            : request && request.url
              ? request.url
              : '';

        if (isCartEndpoint(url) && !isInternalCartRequest()) {
          scheduleSync();
        }
      } catch (error) {}

      return response;
    };
  }

  function bindEvents() {
    patchFetchOnce();

    document.addEventListener('cart:change', scheduleSync);
    document.addEventListener('cart:updated', scheduleSync);
    document.addEventListener('cart:refresh', scheduleSync);
    document.addEventListener('cart:rerendered', scheduleSync);

    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleSync);
      }
    } catch (error) {}

    document.addEventListener('drawer:open', function (event) {
      if (!event.detail || event.detail.id === 'CartDrawer') {
        scheduleSync();
      }
    });

    document.addEventListener('click', function (event) {
      const cartAction = event.target.closest(
        'button[name="plus"], button[name="minus"], button[name="remove"], a[href*="/cart/change"], a[href*="cart/change"], [data-cart-remove]'
      );

      if (cartAction) {
        setTimeout(scheduleSync, 350);
      }
    });
  }

  function boot() {
    bindEvents();
    setTimeout(syncGift, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();